import { getBackendGitCommitHash } from '@/apis/backendGitCommitHash';
import { putUserDetails } from '@/apis/userDetails';
import { environment, frontendCommitHash, iframeParentWhiteList } from '@/config';
import { useAuth0 } from '@auth0/auth0-react';
import { atom, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useEffect } from 'react';
import { useLocation } from 'react-router';

export const backendGitCommitAtom = atom('');

export const DEFAULT_TIMEOUT_PERIOD = 6; // In seconds.

const DAY_LENGTH = 1000 * 60 * 60 * 24; // In milliseconds.

export const DEFAULT_AUTO_PROGRESS_IN_CALLING_VIEW = true;

export const DEFAULT_SHOW_NOT_RENEWING_FIELD = false;

const defaultExpiryTime = new Date().getTime() + DAY_LENGTH;

// This is a nice function that tells the user the amount of time left over before
// Auth0 expiry time happens.

// function printableExpiryTime(leftOverInMS: number) {
//   const HOURS_IN_MILLISECONDS = 3600000;
//   const hoursLeftOver = Math.floor(leftOverInMS / HOURS_IN_MILLISECONDS);
//   const minutesLeftOver = Math.floor((leftOverInMS % HOURS_IN_MILLISECONDS) / MINUTE_LENGTH);
//   const printableTime = `The time left over is ${hoursLeftOver} hours and ${minutesLeftOver} minutes.`;
//   return printableTime;
// }

// This is for the salad bar time out period.

export const timeoutPeriodAtom = atomWithStorage('timeoutPeriod', DEFAULT_TIMEOUT_PERIOD);
export const autoProgressInCallingViewAtom = atomWithStorage(
  'autoProgressInCallingView',
  DEFAULT_AUTO_PROGRESS_IN_CALLING_VIEW
);

// This is the expiry time (in the number of milliseconds since beginning of
// January 1, 1970, UTC) for the Auth0 token. The default is 24 hours after
// now (whenever it is).

export const auth0ExpiryTimeAtom = atom(defaultExpiryTime);

export const rocketCacheBustingIdAtom = atom('g_' + (frontendCommitHash?.substring(0, 7) ?? ''));

export const useUpdateUserPicture = () => {
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();

  useEffect(() => {
    const abortController = new AbortController();
    const updateUserPicture = async () => {
      try {
        if (isAuthenticated && user?.sub && user.picture) {
          const token = await getAccessTokenSilently();
          await putUserDetails(token, user, abortController.signal);
        }
      } catch {
        // performFetch already marks the backend down. Rethrowing this
        // background update becomes a Vite overlay on top of the outage UI.
      }
    };
    updateUserPicture();
    return function cleanup() {
      if (abortController) abortController.abort();
    };
  }, [getAccessTokenSilently, isAuthenticated, user]);
};

export const useSetBackendGitCommitHash = () => {
  const setBackendGitCommit = useSetAtom(backendGitCommitAtom);

  // We only fetch the backend's git commit hash on first render
  useEffect(() => {
    let abortController: AbortController;

    const fetchAndSet = async () => {
      abortController = new AbortController();
      try {
        const hash = await getBackendGitCommitHash(abortController.signal);
        setBackendGitCommit(hash);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          console.debug('Previous getBackendGitCommitHash request was cancelled');
        }
        // performFetch already marks the backend down. Rethrowing this probe
        // becomes a Vite overlay on top of the outage UI.
      }
    };
    if (['local', 'dev'].includes(environment)) {
      fetchAndSet();
    }

    return function cleanup() {
      if (abortController) abortController.abort();
    };
  }, [setBackendGitCommit]);
};

export const useAppAsIFrame = () => {
  const location = useLocation();
  // If this app is in an iframe, and the app is on a permissible URL, send information
  // to them.
  useEffect(() => {
    const parentLocation =
      window.location !== window.parent.location ? document.referrer : document.location.href;
    const currentURL = parentLocation ? new URL(parentLocation).origin : '';
    if (window && window.parent && iframeParentWhiteList.includes(currentURL)) {
      window.parent.postMessage({ currentURL: location.pathname }, '*');
    }
  }, [location]);
};
